import "./styles.scss";

import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  IconArchive,
  IconArchiveOff,
  IconCopy,
  IconCreditCard,
  IconDotsVertical,
  IconInfinity,
  IconPigMoney,
  IconPlus,
  IconQrcode,
  IconRefresh,
  IconRepeat,
  IconRotateClockwise,
  IconSettings,
  IconShare2,
  IconTrash,
} from "@tabler/icons-react";

import Parser from "@helpers/parser";
import TabsSelector from "@components/TabsSelector";
import { Table, Button, Text, Popper, StatusChip, Tooltip } from "@fragments";

import { PLAN_TABS, getPlanTabs, isPlanArchived } from "../enums/planTabs";

import useConfigHeader from "@pages/CommunityConfig/hooks/useConfigHeader";
import ConfigHeaderActions from "@pages/CommunityConfig/components/ConfigHeaderActions";

export default function PricingView({
  plans,
  counts,
  loading,
  activeTab,
  changeTab,
  onCreate,
  onConfigure,
  onShare,
  onCopyLink,
  onArchive,
  onReactivate,
  onDelete,
}) {
  const { t } = useTranslation("settings");
  const popperRef = useRef();
  const archivedTab = activeTab === PLAN_TABS.ARCHIVED;

  // Page identity lives in the floating settings header (the primary action
  // is portaled into it below).
  useConfigHeader({ title: t("pricing.title"), icon: <IconRotateClockwise /> });

  // `t` changes identity on a language switch, so the labels re-resolve.
  const tabs = useMemo(
    () =>
      getPlanTabs(t).map((tab) => ({
        ...tab,
        countBadge: counts[tab.key] > 0 ? counts[tab.key] : undefined,
      })),
    [t, counts],
  );

  function openRowMenu(event, plan) {
    const archived = isPlanArchived(plan);

    const options = [
      {
        id: "configure",
        label: t("shared.configure"),
        icon: <IconSettings size={16} />,
        onClick: async () => {
          popperRef.current.close();
          onConfigure(plan.id);
        },
      },
      archived
        ? {
            id: "reactivate",
            label: t("pricing.reactivate.menu"),
            icon: <IconArchiveOff size={16} />,
            onClick: async () => {
              await onReactivate(plan);
              popperRef.current.close();
            },
          }
        : {
            id: "archive",
            label: t("pricing.archive.menu"),
            icon: <IconArchive size={16} />,
            onClick: async () => {
              popperRef.current.close();
              onArchive(plan);
            },
          },
      {
        id: "delete",
        label: t("pricing.delete.menu"),
        icon: <IconTrash size={16} />,
        variant: "danger",
        onClick: async () => {
          popperRef.current.close();
          onDelete(plan);
        },
      },
    ];

    popperRef.current.open(options, "bottom", event.currentTarget);
  }

  const columns = useMemo(
    () => [
      {
        id: "recurrence",
        title: t("pricing.columns.recurrence"),
        minWidth: 110,
        customRender: (item) => <RecurrenceBadge plan={item} />,
      },
      {
        id: "label",
        title: t("pricing.columns.plan"),
        grow: 1,
        minWidth: 160,
        customRender: (item) => (
          <div className="plan-label">
            <Text.span contrast={8} maxLines={1}>
              {item.label}
            </Text.span>
            {isPlanArchived(item) && (
              <StatusChip.Default text={t("pricing.archivedChip")} />
            )}
          </div>
        ),
      },
      {
        id: "priceBrl",
        title: t("pricing.columns.priceBrl"),
        grow: 1,
        minWidth: 150,
        customRender: (item) => (
          <Text.span contrast={6}>
            R${Parser.price.brl(item.price)}{" "}
            {item.installments > 1 &&
              t("pricing.installments", {
                installments: item.installments,
                amount: Parser.price.brl(
                  item.installments_list[item.installments - 1] /
                    item.installments,
                ),
              })}
          </Text.span>
        ),
      },
      {
        id: "priceUsd",
        title: t("pricing.columns.priceUsd"),
        minWidth: 110,
        customRender: (item) =>
          item.usd_price ? (
            <Text.span contrast={6}>
              US${Parser.price.brl(item.usd_price)}
            </Text.span>
          ) : (
            <Text.span contrast={5}>—</Text.span>
          ),
      },
      {
        id: "subscribers",
        title: t("pricing.columns.subscribers"),
        minWidth: 100,
        align: "right",
        customRender: (item) => (
          <PlanSubscribers summary={item.subscribersSummary} />
        ),
      },
      {
        id: "methods",
        title: t("pricing.columns.methods"),
        minWidth: 90,
        customRender: (item) => <MethodsRow methods={item.methods} />,
      },
    ],
    [t],
  );

  return (
    <div className="config-page config-general-community config-plans-page">
      <ConfigHeaderActions>
        <Button
          onClick={onCreate}
          endIcon={<IconPlus />}
          text={t("pricing.createPlan")}
          size="small"
          preventLoading
        />
      </ConfigHeaderActions>

      <div className="plans-page-inner px-3 mt-4">
        <div className="plans-tabs">
          <TabsSelector tabs={tabs} value={activeTab} onChange={changeTab} />
        </div>

        <Table
          id="community-pricing-table"
          columns={columns}
          data={plans}
          contained
          // Drag handles alone push this six-column table into horizontal
          // scroll on a laptop; reordering buys nothing here.
          draggable={false}
          loading={loading}
          rowClassName={(item) =>
            isPlanArchived(item) ? "plan-row--archived" : ""
          }
          emptyMessage={t(`pricing.empty.${activeTab}.title`)}
          emptySubtitle={t(`pricing.empty.${activeTab}.subtitle`)}
          emptyIcon={
            archivedTab ? <IconArchive size={36} /> : <IconPigMoney size={36} />
          }
          actionsComponent={(item) => {
            const archived = isPlanArchived(item);

            return (
              <div className="plan-row-actions">
                {archived ? (
                  // An archived plan has no public checkout to share; the
                  // slot carries the way back instead.
                  <Button
                    startIcon={<IconArchiveOff size={16} />}
                    text={t("pricing.reactivate.button")}
                    onClick={() => onReactivate(item)}
                    color="inverse"
                    size="small"
                  />
                ) : (
                  <>
                    <Button
                      icon={<IconShare2 size={16} />}
                      onClick={() => onShare(item)}
                      tooltip={t("common:actions.share")}
                      color="inverse"
                      variant="text"
                      size="small"
                      preventLoading
                    />
                    <Button
                      icon={<IconCopy size={16} />}
                      onClick={() => onCopyLink(item)}
                      tooltip={t("shared.checkoutLink")}
                      color="inverse"
                      variant="text"
                      size="small"
                      preventLoading
                    />
                  </>
                )}
                <Button
                  icon={<IconSettings size={16} />}
                  onClick={() => onConfigure(item.id)}
                  tooltip={t("shared.configure")}
                  color="inverse"
                  variant="text"
                  size="small"
                  preventLoading
                />
                <Button
                  icon={<IconDotsVertical size={16} />}
                  onMouseEnter={(event) => openRowMenu(event, item)}
                  onClick={(event) => openRowMenu(event, item)}
                  tooltip={t("shared.moreOptions")}
                  color="inverse"
                  variant="text"
                  size="small"
                  preventLoading
                />
              </div>
            );
          }}
        />
      </div>

      <Popper controlRef={popperRef} config={{ activateOnHover: true }} />
    </div>
  );
}

function RecurrenceBadge({ plan }) {
  const icon =
    plan.interval === "lifetime" ? (
      <IconInfinity />
    ) : !plan.autorenew ? (
      <IconRefresh />
    ) : (
      <IconRepeat />
    );

  return (
    <span className="interval-badge">
      <Text.span contrast={7} left={icon}>
        {Parser.interval.adjective(plan.interval)}
      </Text.span>
    </span>
  );
}

// Active subscribers, with the full status breakdown on hover/focus.
function PlanSubscribers({ summary }) {
  const { t } = useTranslation("settings");
  const counts = {
    active: 0,
    expired: 0,
    canceled: 0,
    unknown: 0,
    ...(summary || {}),
  };

  const rows = [
    ["active", counts.active],
    ["expired", counts.expired],
    ["canceled", counts.canceled],
  ];
  if (counts.unknown > 0) rows.push(["unknown", counts.unknown]);

  const breakdown = (
    <div className="plan-subscribers-tip">
      {rows.map(([status, value]) => (
        <div className="plan-subscribers-tip__row" key={status}>
          <span>{t(`pricing.subscribers.${status}`)}</span>
          <span>{value}</span>
        </div>
      ))}
    </div>
  );

  return (
    <Tooltip content={breakdown} placement="top">
      <span className="plan-subscribers" tabIndex={0}>
        {/* Text drops falsy children, so a zero must travel as a string. */}
        <Text.span contrast={counts.active > 0 ? 8 : 5}>
          {String(counts.active)}
        </Text.span>
      </span>
    </Tooltip>
  );
}

// Icons only, labels on hover: the row has to fit beside six other columns.
function MethodsRow({ methods }) {
  const { t } = useTranslation("settings");

  const items = [
    { key: "credit", icon: <IconCreditCard size={16} /> },
    { key: "pix", icon: <IconQrcode size={16} /> },
  ];

  return (
    <div className="methods-row">
      {items.map(({ key, icon }) => {
        const enabled = !!methods?.[key]?.enabled;
        return (
          <Tooltip
            key={key}
            content={t(`pricing.methods.${enabled ? "enabled" : "disabled"}`, {
              method: t(`pricing.methods.${key}`),
            })}
          >
            <span className={`method${enabled ? "" : " disabled"}`} tabIndex={0}>
              {icon}
            </span>
          </Tooltip>
        );
      })}
    </div>
  );
}
